import { z } from "zod";
import { IntakeQuestionTypeSchema } from "@src/sudozi-schemas/intake-submission-schemas";
import { DocumentTypeSchema } from "@src/sudozi-schemas/document-type-schema";

const IntakeQuestionCustomSudoziOptionSchema = z.object({
  id: z.string(),
  parentId: z.string(),
  label: z.string(),
  ordinal: z.number(),
});

export const IntakeQuestionSchema = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string(),
  isRequired: z.boolean(),
  showOnVendorDetails: z.boolean(),
  type: IntakeQuestionTypeSchema,
  isMulti: z.boolean(),
  documentTypes: DocumentTypeSchema.array(),
  ordinal: z.number(),
  chainOperator: z.enum(["AND", "OR"]),
  step: z.object({
    id: z.string(),
    label: z.string(),
    ordinal: z.number(),
    isAdditionalStep: z.boolean(),
  }),
  customOptions: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      ordinal: z.number(),
    }),
  ),
  intakeQuestionBusinessUnitOptions: z.array(
    IntakeQuestionCustomSudoziOptionSchema,
  ),
  intakeQuestionDepartmentOptions: z.array(
    IntakeQuestionCustomSudoziOptionSchema,
  ),
  intakeQuestionLocationOptions: z.array(
    IntakeQuestionCustomSudoziOptionSchema,
  ),
  intakeQuestionMemberOptions: z.array(IntakeQuestionCustomSudoziOptionSchema),
  intakeQuestionServiceOptions: z.array(IntakeQuestionCustomSudoziOptionSchema),
  intakeQuestionSpendCategoryOptions: z.array(
    IntakeQuestionCustomSudoziOptionSchema,
  ),
  intakeQuestionVendorOptions: z.array(IntakeQuestionCustomSudoziOptionSchema),
  conditionAnyOfs: z.array(
    z.object({
      id: z.string(),
      values: z.array(z.string()),
      valuesQuestionId: z.string(),
      valuesQuestion: z.object({
        id: z.string(),
        label: z.string(),
        type: IntakeQuestionTypeSchema,
      }),
    }),
  ),
  conditionNumerics: z.array(
    z.object({
      id: z.string(),
      minimumValue: z.number().nullable(),
      maximumValue: z.number().nullable(),
      valuesQuestionId: z.string(),
      valuesQuestion: z.object({
        id: z.string(),
        label: z.string(),
        type: IntakeQuestionTypeSchema,
      }),
    }),
  ),
  conditionBooleans: z.array(
    z.object({
      id: z.string(),
      value: z.boolean(),
      valuesQuestionId: z.string(),
      valuesQuestion: z.object({
        id: z.string(),
        label: z.string(),
        type: IntakeQuestionTypeSchema,
      }),
    }),
  ),
  requireDepartmentApproverAssigneeSelection: z.boolean(),
});

export const CreateIntakeQuestionSchema = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string(),
  ordinal: z.number(),
  isRequired: z.boolean(),
  isMulti: z.boolean(),
  chainOperator: z.enum(["AND", "OR"]),
  type: IntakeQuestionTypeSchema,
  customOptions: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      ordinal: z.number(),
    }),
  ),
  intakeQuestionBusinessUnitOptions: z.array(
    z.object({
      id: z.string(),
      parentId: z.string(),
      label: z.string(),
      ordinal: z.number(),
    }),
  ),
  intakeQuestionDepartmentOptions: z.array(
    z.object({
      id: z.string(),
      parentId: z.string(),
      label: z.string(),
      ordinal: z.number(),
    }),
  ),
  intakeQuestionLocationOptions: z.array(
    z.object({
      id: z.string(),
      parentId: z.string(),
      label: z.string(),
      ordinal: z.number(),
    }),
  ),
  intakeQuestionMemberOptions: z.array(
    z.object({
      id: z.string(),
      parentId: z.string(),
      label: z.string(),
      ordinal: z.number(),
    }),
  ),
  intakeQuestionSpendCategoryOptions: z.array(
    z.object({
      id: z.string(),
      parentId: z.string(),
      label: z.string(),
      ordinal: z.number(),
    }),
  ),
  intakeQuestionServiceOptions: z.array(
    z.object({
      id: z.string(),
      parentId: z.string(),
      label: z.string(),
      ordinal: z.number(),
    }),
  ),
  intakeQuestionVendorOptions: z.array(
    z.object({
      id: z.string(),
      parentId: z.string(),
      label: z.string(),
      ordinal: z.number(),
    }),
  ),
  numericPreConditions: z.array(
    z.object({
      id: z.string(),
      minimumValue: z.number().nullable(),
      maximumValue: z.number().nullable(),
      valuesQuestionId: z.string(),
    }),
  ),
  anyOfPreConditions: z.array(
    z.object({
      id: z.string(),
      values: z.array(z.string()),
      valuesQuestionId: z.string(),
    }),
  ),
  booleanPreConditions: z.array(
    z.object({
      id: z.string(),
      value: z.boolean(),
      valuesQuestionId: z.string(),
    }),
  ),
});
export type CreateIntakeQuestionSchemaType = z.infer<
  typeof CreateIntakeQuestionSchema
>;

export type IntakeQuestionSchemaType = z.infer<typeof IntakeQuestionSchema>;
